/*
 * PPShop — the bridge between the store design's logic and Shopify.
 * Liquid (layout/theme.liquid) fills window.PP before this runs:
 *   PP.route    { view, cat, pid, page, post }      which design view this URL shows
 *   PP.products { [sku]: { handle, variant, price, compare, available } }   prices in rupees
 *   PP.cart     [{ sku, key, variant, qty }]
 *   PP.blog     [{ handle, title, exam, topic, product, date, read, excerpt, image }]
 *   PP.article  { handle, ..., html }               only on article pages
 *   PP.settings { ... theme settings ... }
 *   PP.assets   { logo, ... }
 */
(function () {
  'use strict';
  var PP = window.PP = window.PP || {};
  PP.products = PP.products || {};
  PP.settings = PP.settings || {};
  PP.route = PP.route || { view: 'home' };

  // design id → Shopify collection handle
  var COLL = { all: 'all', c10: 'class-10', c1112: 'class-11-12', c12: 'class-12-boards', neet: 'neet', jee: 'jee', cuet: 'cuet', extras: 'add-ons' };
  var COLL_REV = {}; Object.keys(COLL).forEach(function (k) { COLL_REV[COLL[k]] = k; });
  // design page key → URL
  var PAGE_URL = { about: '/pages/about', faq: '/pages/faq', refund: '/pages/refund-policy', delivery: '/pages/delivery-policy', terms: '/pages/terms', privacy: '/pages/privacy-policy' };
  var BLOG = '/blogs/blog';

  function root() { return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/'; }
  function abs(p) { return root().replace(/\/$/, '') + p; }
  function handleOf(sku) { var p = PP.products[sku]; return p ? p.handle : sku; }

  function urlFor(view, s) {
    switch (view) {
      case 'home': return abs('/');
      case 'collection': return abs('/collections/' + (COLL[s.cat] || 'all'));
      case 'product': return abs('/products/' + handleOf(s.pid));
      case 'page': return abs(PAGE_URL[s.page] || PAGE_URL.about);
      case 'contact': return abs('/pages/contact');
      case 'track': return abs('/pages/redownload');
      case 'blog': return abs(s.post ? BLOG + '/' + s.post : BLOG);
      default: return null;
    }
  }
  function samePath(url) {
    var a = document.createElement('a'); a.href = url;
    return a.pathname.replace(/\/$/, '') === location.pathname.replace(/\/$/, '');
  }

  // ---------- cart (Shopify AJAX API) ----------
  function json(url, body) {
    return fetch(abs(url), {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: body ? JSON.stringify(body) : undefined
    }).then(function (r) {
      return r.json().then(function (j) { if (!r.ok) { var e = new Error(j.description || j.message || 'Request failed'); e.data = j; throw e; } return j; });
    });
  }
  function skuOfItem(it) { return it.sku || (it.properties && it.properties._sku) || it.handle; }
  function normalize(cart) {
    PP.cartRaw = cart;
    return (cart.items || []).map(function (it) { return { sku: skuOfItem(it), key: it.key, variant: it.variant_id, qty: it.quantity }; });
  }
  function ids(items) { var seen = {}; return items.map(function (i) { return i.sku; }).filter(function (s) { if (seen[s]) return false; seen[s] = 1; return true; }); }

  var cartItems = (PP.cart || []).slice();
  var queue = Promise.resolve();
  function serial(fn) { queue = queue.then(fn, fn); return queue; }

  var PPShop = {
    COLL: COLL, COLL_REV: COLL_REV, PAGE_URL: PAGE_URL,
    settings: PP.settings,
    route: PP.route,
    urlFor: urlFor,
    /** Navigate to a design view. Returns true when a full page load was started. */
    navigate: function (view, s) {
      var url = urlFor(view, s);
      if (!url || samePath(url)) return false;
      location.assign(url);
      return true;
    },
    cartIds: function () { return ids(cartItems); },
    variantOf: function (sku) { var p = PP.products[sku]; return p && p.variant; },
    available: function (sku) { var p = PP.products[sku]; return !!(p && p.variant && p.available !== false); },
    refreshCart: function () {
      return json('/cart.js').then(function (c) { cartItems = normalize(c); return ids(cartItems); });
    },
    add: function (skus) {
      skus = [].concat(skus).filter(function (s) { return PPShop.available(s) && !cartItems.some(function (i) { return i.sku === s; }); });
      if (!skus.length) return Promise.resolve(ids(cartItems));
      return serial(function () {
        return json('/cart/add.js', { items: skus.map(function (s) { return { id: PPShop.variantOf(s), quantity: 1 }; }) })
          .then(function () { return PPShop.refreshCart(); });
      });
    },
    remove: function (sku) {
      var it = cartItems.filter(function (i) { return i.sku === sku; })[0];
      if (!it) return Promise.resolve(ids(cartItems));
      return serial(function () {
        return json('/cart/change.js', { id: it.key, quantity: 0 }).then(function (c) { cartItems = normalize(c); return ids(cartItems); });
      });
    },
    /** Save order notes (rewards, coupon) on the cart, then go to Shopify checkout. */
    checkout: function (opts) {
      opts = opts || {};
      var attrs = opts.attributes || {};
      return json('/cart/update.js', { attributes: attrs }).catch(function () {}).then(function () {
        var url = abs('/checkout');
        if (opts.discount) url += '?discount=' + encodeURIComponent(opts.discount);
        location.assign(url);
      });
    },

    /** Remember a discount code for this visit and attach it to the Shopify session, so it reaches checkout. */
    savedCoupon: function () { try { return sessionStorage.getItem('ppCoupon') || ''; } catch (e) { return ''; } },
    applyDiscount: function (code) {
      try { sessionStorage.setItem('ppCoupon', code); } catch (e) {}
      return fetch(abs('/discount/' + encodeURIComponent(code)), { credentials: 'same-origin', redirect: 'manual' }).catch(function () {});
    },

    // ---------- forms ----------
    /** Newsletter signup (Shopify customer with accepts_marketing + tags). */
    subscribe: function (email, tags) {
      var fd = new FormData();
      fd.append('form_type', 'customer'); fd.append('utf8', '✓');
      fd.append('contact[email]', email);
      fd.append('contact[tags]', ['newsletter'].concat(tags || []).join(','));
      return post('/contact', fd);
    },
    contact: function (fields) {
      var fd = new FormData();
      fd.append('form_type', 'contact'); fd.append('utf8', '✓');
      Object.keys(fields).forEach(function (k) { fd.append('contact[' + k + ']', fields[k]); });
      return post('/contact', fd);
    },
    /** Ask the delivery service to resend download links; falls back to a contact message. */
    redownload: function (value) {
      var api = (PP.settings.deliveryApi || '').replace(/\/$/, '');
      var isEmail = /\S+@\S+\.\S+/.test(value);
      if (api) {
        return fetch(api + '/resend', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(isEmail ? { email: value } : { order: value }) })
          .then(function (r) { if (!r.ok && r.status !== 429) throw new Error('resend failed'); return true; });
      }
      return PPShop.contact({ email: isEmail ? value : '', name: 'Re-download request', body: 'Please resend my download links. Email or order ID: ' + value });
    },

    /** Policy / info page content: the Shopify page's HTML when it has any, else the design's copy. */
    pageData: function (key, PAGES) {
      var base = PAGES[key] || null, pg = PP.page;
      if (pg && pg.html && pg.html.replace(/<[^>]+>/g, '').trim()) {
        var doc = new DOMParser().parseFromString('<div>' + pg.html + '</div>', 'text/html');
        var intro = '', sec = [], cur = null;
        [].forEach.call(doc.body.firstChild.children, function (el) {
          var t = (el.textContent || '').replace(/\s+/g, ' ').trim(); if (!t) return;
          if (/^H[2-4]$/.test(el.tagName)) { cur = [t, '']; sec.push(cur); }
          else if (cur) cur[1] = cur[1] ? cur[1] + ' ' + t : t;
          else intro = intro ? intro + ' ' + t : t;
        });
        return { title: pg.title || (base && base.title) || '', intro: intro, sec: sec };
      }
      return base || { title: pg ? pg.title : '', intro: '', sec: [] };
    },

    // ---------- blog ----------
    blogIndex: function () { return PP.blog || []; },
    article: function () { return PP.article || null; },
    blogImage: function (handle, kind) {
      var a = (PP.blog || []).filter(function (x) { return x.handle === handle; })[0] || (PP.article && PP.article.handle === handle ? PP.article : null);
      if (kind === 'hero' && a && a.image) return a.image;
      var base = PP.settings.blogImagesBase || '';
      return base ? base.replace(/\/$/, '') + '/' + handle + '-' + kind + '.webp' : '';
    },
    /** Convert a Shopify article's HTML into the design's block list. */
    htmlToBlocks: function (html) {
      var doc = new DOMParser().parseFromString('<div>' + (html || '') + '</div>', 'text/html');
      var out = [];
      var text = function (el) { return (el.textContent || '').replace(/\s+/g, ' ').trim(); };
      var lead = function (li) {
        var first = li.firstElementChild;
        if (first && first.tagName === 'STRONG' && li.innerHTML.trim().indexOf('<strong>') === 0) {
          var b = text(first); return { b: b + ' ', t: text(li).slice(b.length).trim() };
        }
        return { b: '', t: text(li) };
      };
      [].forEach.call(doc.body.firstChild.children, function (el) {
        var tag = el.tagName;
        if (tag === 'SCRIPT' || tag === 'STYLE') return;
        if (el.classList.contains('ppn-cta')) return out.push({ isCta: true });
        if (el.classList.contains('ppn-link')) { var a = el.querySelector('a'); if (a) return out.push({ isLink: true, text: text(a).replace(/\s*↗$/, ''), href: a.getAttribute('href') }); }
        if (tag === 'H2') return out.push({ isH2: true, text: text(el) });
        if (tag === 'H3' || tag === 'H4') return out.push({ isH3: true, text: text(el) });
        if (tag === 'BLOCKQUOTE') return out.push({ isQuote: true, text: text(el) });
        if (tag === 'FIGURE' || (tag === 'P' && el.children.length === 1 && el.firstElementChild.tagName === 'IMG' && !text(el))) {
          var img = el.querySelector('img'); if (!img) return;
          var cap = el.querySelector('figcaption');
          return out.push({ isImg: true, src: img.getAttribute('src'), alt: img.getAttribute('alt') || '', cap: cap ? text(cap) : '' });
        }
        if (tag === 'UL' || tag === 'OL') {
          return out.push({ isList: true, items: [].map.call(el.querySelectorAll(':scope > li'), function (li, i) { var s = lead(li); return { mark: tag === 'UL' ? '•' : (i + 1) + '.', b: s.b, t: s.t }; }) });
        }
        if (tag === 'P' && el.querySelector('em') && /accuracy reports/i.test(text(el)) && /notes vertical/i.test(text(el))) return; // footer line shown by the design's own box
        var t = text(el); if (t) out.push({ isP: true, text: t });
      });
      return out;
    }
  };

  function post(url, fd) {
    return fetch(abs(url), { method: 'POST', body: fd, credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then(function (r) {
        // Shopify answers with a redirect to ?customer_posted=true / ?contact_posted=true, or to /challenge when it wants a captcha.
        if (/\/challenge/.test(r.url)) {
          // Shopify wants a captcha: submit the same form for real so the shopper can solve it.
          var f = document.createElement('form'); f.method = 'post'; f.action = abs(url); f.style.display = 'none';
          fd.forEach(function (v, k) { var i = document.createElement('input'); i.type = 'hidden'; i.name = k; i.value = v; f.appendChild(i); });
          document.body.appendChild(f); f.submit();
          var e = new Error('challenge'); e.challenge = r.url; throw e;
        }
        if (!r.ok) throw new Error('form failed');
        return true;
      });
  }

  window.PPShop = PPShop;
})();
